import React from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'

type UserRole = 'admin' | 'instructor' | 'affiliate' | 'student' | 'super_admin'

interface RoleBasedRouteProps {
    children: React.ReactNode
    allowedRoles: UserRole[]
    redirectTo?: string
}

/**
 * A route component that restricts access based on user role.
 * If the user's role is not in the allowedRoles list, they will be redirected.
 */
export const RoleBasedRoute: React.FC<RoleBasedRouteProps> = ({
    children,
    allowedRoles,
    redirectTo
}) => {
    const { user, isAuthenticated, isLoading } = useAuth()

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-background-light dark:bg-background-dark">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                    <p className="mt-4 text-gray-500 dark:text-gray-400">Loading...</p>
                </div>
            </div>
        )
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />
    }

    const userRole = user?.role as UserRole

    if (!allowedRoles.includes(userRole)) {
        // Redirect to appropriate dashboard based on role
        const roleRedirects: Record<UserRole, string> = {
            admin: '/admin',
            super_admin: '/admin',
            instructor: '/instructor',
            affiliate: '/affiliate',
            student: '/dashboard'
        }

        const targetRedirect = redirectTo || roleRedirects[userRole] || '/dashboard'
        return <Navigate to={targetRedirect} replace />
    }

    return <>{children}</>
}

export default RoleBasedRoute
